import { Expense } from "./types";

const toDayStartMs = (value: Date | string | undefined): number | null => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  // Use UTC to avoid local timezone day-boundary surprises.
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
};

export const getExpenseSeriesRootId = (expense: Expense): string =>
  expense.originalId || expense.id;

export const isRecurringSeriesExpense = (expense: Expense): boolean =>
  Boolean(
    expense.isRecurring || expense.isRecurringInstance || expense.originalId
  );

export const isSeriesOccurrence = (expense: Expense): boolean =>
  Boolean(expense.isRecurringInstance || expense.originalId) &&
  !Boolean(expense.isRecurring);

export const didAmountChange = (
  prev: Pick<Expense, "amount">,
  next: Pick<Expense, "amount">
): boolean => Number(prev.amount) !== Number(next.amount);

export const didDueDateDayChange = (
  prev: Expense,
  next: Pick<Expense, "dueDate">
): boolean => {
  const prevMs = toDayStartMs(prev.dueDate);
  const nextMs = toDayStartMs(next.dueDate);
  if (prevMs === null || nextMs === null) return false;
  return prevMs !== nextMs;
};

export const findSeriesOccurrenceByDueDate = (
  expenses: Expense[],
  seriesRootId: string,
  dueDate: Date
): Expense | null => {
  const targetMs = toDayStartMs(dueDate);
  if (targetMs === null) return null;
  return (
    expenses.find((e) => {
      if (getExpenseSeriesRootId(e) !== seriesRootId) return false;
      const dueMs = toDayStartMs(e.dueDate);
      return dueMs === targetMs;
    }) || null
  );
};

export const getExpenseSeriesOccurrences = (
  expenses: Expense[],
  targetExpense: Expense
): Expense[] => {
  const seriesRootId = getExpenseSeriesRootId(targetExpense);
  const deduped = new Map<string, Expense>();

  expenses.forEach((expense) => {
    if (getExpenseSeriesRootId(expense) !== seriesRootId) return;
    deduped.set(expense.id, expense);
  });

  return Array.from(deduped.values()).sort((a, b) => {
    const aMs = toDayStartMs(a.dueDate) ?? 0;
    const bMs = toDayStartMs(b.dueDate) ?? 0;
    return aMs - bMs;
  });
};

export const getFutureExpenseSeriesOccurrences = (
  expenses: Expense[],
  targetExpense: Expense
): Expense[] => {
  const targetMs = toDayStartMs(targetExpense.dueDate);
  if (targetMs === null) return [];
  return getExpenseSeriesOccurrences(expenses, targetExpense).filter(
    (expense) => (toDayStartMs(expense.dueDate) ?? 0) > targetMs
  );
};

export const getAvailableSeriesRoots = (expenses: Expense[]): Expense[] =>
  expenses.filter(
    (e) => e.isRecurring && !e.isRecurringInstance && e.recurrencePattern
  );
